import type {
  Brand,
  Client,
  Contract,
  Contractor,
  GetContractsQuery,
  GetOrdersQuery,
  GetOtherDocumentsQuery,
  Order,
  OtherDocument,
  Project,
} from "@src/__generated__/graphql";
import { MediaModel } from "@src/utils/components/dropzone";

type TContract = NonNullable<GetContractsQuery["contracts"]>["data"][0];
type TOrder = NonNullable<GetOrdersQuery["orders"]>["data"][0];
type TOutgoingInvoices = NonNullable<TOrder["outgoingInvoices"]>;
type TOtherDocument = NonNullable<
  GetOtherDocumentsQuery["otherDocuments"]
>["data"][0];
type TProjects = NonNullable<NonNullable<TContract["projects"]>[0]>;
type TBrand = NonNullable<NonNullable<TContract["brands"]>[0]>;
type TContractor = NonNullable<NonNullable<TContract["contractors"]>[0]>;
type TClient = NonNullable<NonNullable<TContract["clients"]>[0]>;

export class ContractModel {
  id: Contract["id"];
  title: string;
  validFrom: Date;
  validTo?: Date;
  type: Contract["type"];
  projects: ProjectsModel[];
  brands: BrandModel[];
  contractors: ContractorModel[];
  clients: ClientModel[];
  files: MediaModel[];
  urls: Partial<MediaModel["urls"]>;
  cloudUrl: TContract["cloudUrl"];
  driveCloudUrl: TContract["driveCloudUrl"];
  constructor(src: TContract) {
    this.id = src.id;
    this.type = src.type;
    this.title = src.title;
    this.validFrom = new Date(src.valid_from);
    this.validTo = src.valid_to ? new Date(src.valid_to) : undefined;
    this.brands =
      src.brands
        ?.filter((b): b is TBrand => !!b)
        .map((b) => new BrandModel(b)) ?? [];
    this.projects =
      src.projects
        ?.filter((p): p is TProjects => !!p)
        .map((p) => new ProjectsModel(p)) ?? [];
    this.contractors =
      src.contractors
        ?.filter((c): c is TContractor => !!c)
        .map((c) => new ContractorModel(c)) ?? [];
    this.clients =
      src.clients
        ?.filter((c): c is TClient => !!c)
        .map((c) => new ClientModel(c)) ?? [];
    this.files = src.files.map((f) => new MediaModel(f));
    this.cloudUrl = src.cloudUrl;
    this.driveCloudUrl = src.driveCloudUrl;
    this.urls = src.urls;
  }

  static fromGetContractsQuery(src: GetContractsQuery["contracts"]) {
    return (
      src?.data.map((i) => {
        const {
          id,
          brands,
          projects,
          type,
          title,
          valid_from,
          valid_to,
          files,
          clients,
          contractors,
          urls,
          cloudUrl,
          driveCloudUrl,
        } = i;

        return new ContractModel({
          id,
          brands,
          projects,
          type,
          title,
          valid_from,
          valid_to,
          files,
          clients,
          contractors,
          urls,
          cloudUrl,
          driveCloudUrl,
        });
      }) ?? []
    );
  }
}

export class OrderModel {
  id: Order["id"];
  outgoingInvoices: TOutgoingInvoices;
  purchase_order_no: Order["purchase_order_no"];
  date_of_order?: Date;
  projects: ProjectsModel[];
  brand: BrandModel;
  files: MediaModel[];
  urls: Partial<MediaModel["urls"]>;
  cloudUrl: TOrder["cloudUrl"];
  driveCloudUrl: TOrder["driveCloudUrl"];
  constructor(src: TOrder) {
    this.id = src.id;
    this.purchase_order_no = src.purchase_order_no;
    this.outgoingInvoices = src.outgoingInvoices;
    if (src.date_of_order) this.date_of_order = new Date(src.date_of_order);
    this.brand = new BrandModel(src.brand);
    this.projects =
      src.projects
        ?.filter((p): p is TProjects => !!p)
        .map((p) => new ProjectsModel(p)) ?? [];
    this.files = src.files.map((f) => new MediaModel(f));
    this.urls = src.urls;
    this.cloudUrl = src.cloudUrl;
    this.driveCloudUrl = src.driveCloudUrl;
  }

  static fromGetOrdersQuery(src: GetOrdersQuery["orders"]) {
    return (
      src?.data.map((i) => {
        const {
          id,
          outgoingInvoices,
          purchase_order_no,
          date_of_order,
          brand,
          projects,
          files,
          urls,
          cloudUrl,
          driveCloudUrl,
        } = i;

        return new OrderModel({
          id,
          brand,
          outgoingInvoices,
          date_of_order,
          purchase_order_no,
          projects,
          files,
          urls,
          cloudUrl,
          driveCloudUrl,
        });
      }) ?? []
    );
  }
}

export class OtherDocumentModel {
  id: OtherDocument["id"];
  title: OtherDocument["title"];
  projects: ProjectsModel[];
  brands: BrandModel[];
  contractors: ContractorModel[];
  clients: ClientModel[];
  files: MediaModel[];
  urls: Partial<MediaModel["urls"]>;
  cloudUrl: TOtherDocument["cloudUrl"];
  driveCloudUrl: TOtherDocument["driveCloudUrl"];
  constructor(src: TOtherDocument) {
    this.id = src.id;
    this.title = src.title;
    this.brands =
      src.brands
        ?.filter((b): b is TBrand => !!b)
        .map((b) => new BrandModel(b)) ?? [];
    this.projects =
      src.projects
        ?.filter((p): p is TProjects => !!p)
        .map((p) => new ProjectsModel(p)) ?? [];
    this.projects =
      src.projects
        ?.filter((p): p is TProjects => !!p)
        .map((p) => new ProjectsModel(p)) ?? [];
    this.contractors =
      src.contractors
        ?.filter((c): c is TContractor => !!c)
        .map((c) => new ContractorModel(c)) ?? [];
    this.clients =
      src.clients
        ?.filter((c): c is TClient => !!c)
        .map((c) => new ClientModel(c)) ?? [];
    this.files = src.files.map((f) => new MediaModel(f));
    this.urls = src.urls;
    this.cloudUrl = src.cloudUrl;
    this.driveCloudUrl = src.driveCloudUrl;
  }
  static fromGetOtherDocumentsQuery(
    src: GetOtherDocumentsQuery["otherDocuments"],
  ) {
    return (
      src?.data.map((i) => {
        const {
          id,
          brands,
          title,
          projects,
          files,
          clients,
          contractors,
          urls,
          cloudUrl,
          driveCloudUrl,
        } = i;

        return new OtherDocumentModel({
          id,
          title,
          brands,
          clients,
          contractors,
          projects,
          files,
          urls,
          cloudUrl,
          driveCloudUrl,
        });
      }) ?? []
    );
  }
}

class BrandModel {
  id: Brand["id"];
  name: Brand["name"];
  constructor(src: TBrand) {
    this.name = src.name;
    this.id = src.id;
  }
}
class ClientModel {
  id: Client["id"];
  name: Client["name"];
  constructor(src: TClient) {
    this.id = src.id;
    this.name = src.name;
  }
}

class ContractorModel {
  id: Contractor["id"];
  legalName: Contractor["legal_name"];
  constructor(src: TContractor) {
    this.id = src.id;
    this.legalName = src.legal_name;
  }
}

class ProjectsModel {
  id: Project["id"];
  title: Project["title"];
  code: Project["code"];
  constructor(src: TProjects) {
    this.id = src.id;
    this.title = src.title;
    this.code = src.code;
  }
}
